import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart3, TrendingUp, TrendingDown, Users, AlertTriangle,
  Download, Calendar, Activity, Shield, Target, GraduationCap,
  BookOpen, RefreshCw,
} from 'lucide-react';
import { API_URL } from '@/services/apiClient';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AnalyticsData {
  overview: {
    totalStudents: number;
    totalTeachers: number;
    totalIncidents: number;
    activeAlerts: number;
    incidentTrend: number;
    alertTrend: number;
  };
  incidentsByType: Array<{ type: string; count: number }>;
  incidentsBySeverity: Array<{ severity: string; count: number }>;
  incidentsByMonth: Array<{ month: string; count: number }>;
  topStudents: Array<{ name: string; incidentCount: number }>;
  teacherActivity: Array<{ name: string; resolvedCount: number }>;
}

type Preset = '1W' | '1M' | '3M' | '6M' | '1Y' | 'custom';

const PRESETS: Array<{ key: Preset; label: string; days: number }> = [
  { key: '1W',     label: '1 Week',   days: 7   },
  { key: '1M',     label: '1 Month',  days: 30  },
  { key: '3M',     label: '3 Months', days: 90  },
  { key: '6M',     label: '6 Months', days: 180 },
  { key: '1Y',     label: '1 Year',   days: 365 },
  { key: 'custom', label: 'Custom',   days: 30  },
];

const TYPE_COLORS: Record<string, string> = {
  cyberbullying: 'bg-purple-500',
  self_harm:     'bg-red-500',
  violence:      'bg-orange-500',
  threat:        'bg-amber-500',
  profanity:     'bg-yellow-500',
  hate_speech:   'bg-pink-500',
};

const SEVERITY_BAR: Record<string, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-500',
  medium:   'bg-amber-500',
  low:      'bg-blue-500',
};

const SEVERITY_TEXT: Record<string, string> = {
  critical: 'text-red-600',
  high:     'text-orange-600',
  medium:   'text-amber-600',
  low:      'text-blue-600',
};

// ── SVG Line Chart ─────────────────────────────────────────────────────────────

function LineTrendChart({ data, dm }: { data: Array<{ month: string; count: number }>; dm: boolean }) {
  if (data.length === 0) return null;
  const W = 600;
  const padX = 36, padTop = 30, padBottom = 26, innerH = 120;
  const totalH = padTop + innerH + padBottom;
  const max = Math.max(...data.map(d => d.count), 1);
  const pts = data.map((d, i) => ({
    x: padX + (i / Math.max(data.length - 1, 1)) * (W - padX * 2),
    y: padTop + (1 - d.count / max) * innerH,
    month: d.month,
    count: d.count,
  }));
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const areaPath = `M${pts[0].x},${padTop + innerH} ` +
    pts.map(p => `L${p.x},${p.y}`).join(' ') +
    ` L${pts[pts.length - 1].x},${padTop + innerH} Z`;
  const gridColor  = dm ? '#334155' : '#e2e8f0';
  const labelColor = dm ? '#94a3b8' : '#94a3b8';

  return (
    <svg viewBox={`0 0 ${W} ${totalH}`} className="w-full" style={{ height: totalH }}>
      <defs>
        <linearGradient id="areaGradAnalytics" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity={dm ? '0.25' : '0.15'} />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map(g => {
        const y = padTop + (1 - g) * innerH;
        return (
          <g key={g}>
            <line x1={padX} y1={y} x2={W - padX} y2={y} stroke={gridColor} strokeWidth="1" strokeDasharray="4 3" />
            <text x={padX - 6} y={y + 4} textAnchor="end" fontSize="9" fill={labelColor}>
              {Math.round(g * max)}
            </text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#areaGradAnalytics)" />
      <polyline points={polyline} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#3b82f6" stroke={dm ? '#1e293b' : '#ffffff'} strokeWidth="2" />
          {p.count > 0 && (
            <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="10" fill="#3b82f6" fontWeight="600">{p.count}</text>
          )}
        </g>
      ))}
      {pts.map(p => (
        <text key={`lbl-${p.month}`} x={p.x} y={totalH - 4} textAnchor="middle" fontSize="10" fill={labelColor}>
          {p.month}
        </text>
      ))}
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function isoToInput(iso: string) { return iso.split('T')[0]; }
function inputToDisplay(iso: string) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}
function daysAgoISO(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoToInput(d.toISOString());
}
function todayISO() { return isoToInput(new Date().toISOString()); }
function diffDays(from: string, to: string) {
  return Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000));
}

// ── Main ───────────────────────────────────────────────────────────────────────

export function AnalyticsDashboard({ dm = false }: { dm?: boolean }) {
  const [analytics, setAnalytics]         = useState<AnalyticsData | null>(null);
  const [loading, setLoading]             = useState(true);
  const [preset, setPreset]               = useState<Preset>('3M');
  const [fromDate, setFromDate]           = useState(() => daysAgoISO(90));
  const [toDate, setToDate]               = useState(() => todayISO());
  const [studentTeachers, setStudentTeachers] = useState<Map<string, string>>(new Map());

  const doFetch = async (days: number) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/dashboard/school-admin/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAnalytics(await res.json());
    } catch { /* silently fail */ } finally {
      setLoading(false);
    }
  };

  // On mount: initial fetch + classroom teacher mapping
  useEffect(() => {
    doFetch(90);
    const token = sessionStorage.getItem('authToken');
    fetch(`${API_URL}/dashboard/school-admin/classrooms`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { classrooms: [] })
      .then(data => {
        const map = new Map<string, string>();
        for (const cls of data.classrooms || []) {
          const teacher = cls.teacherName || cls.teacher?.name || cls.teacher?.user?.name || '';
          for (const s of cls.students || []) {
            map.set(s.name, teacher);
          }
        }
        setStudentTeachers(map);
      })
      .catch(() => {});
  }, []);

  const handlePresetChange = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const days = PRESETS.find(x => x.key === p)!.days;
      const from = daysAgoISO(days);
      const to   = todayISO();
      setFromDate(from);
      setToDate(to);
      doFetch(days);
    }
  };

  const handleApply = () => {
    if (!fromDate || !toDate) return;
    doFetch(diffDays(fromDate, toDate));
  };

  const handleExportCSV = () => {
    if (!analytics) return;
    const rows: string[][] = [
      ['ShieldHaven Analytics Export'],
      ['Period', `${inputToDisplay(fromDate)} – ${inputToDisplay(toDate)}`],
      [],
      ['Metric', 'Value'],
      ['Total Students',  String(analytics.overview.totalStudents)],
      ['Total Teachers',  String(analytics.overview.totalTeachers)],
      ['Total Incidents', String(analytics.overview.totalIncidents)],
      ['Active Alerts',   String(analytics.overview.activeAlerts)],
      [],
      ['Top 3 Incident Types', ''],
      ['Type', 'Count'],
      ...[...analytics.incidentsByType].sort((a, b) => b.count - a.count).slice(0, 3).map(i => [i.type, String(i.count)]),
      [],
      ['Top 3 Severity Levels', ''],
      ['Severity', 'Count'],
      ...[...analytics.incidentsBySeverity].sort((a, b) => b.count - a.count).slice(0, 3).map(i => [i.severity, String(i.count)]),
      [],
      ['Monthly Trend', ''],
      ['Month', 'Count'],
      ...analytics.incidentsByMonth.map(i => [i.month, String(i.count)]),
    ];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `analytics-${todayISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const top3Type     = [...(analytics?.incidentsByType || [])].sort((a, b) => b.count - a.count).slice(0, 3);
  const maxTypeCount = Math.max(...top3Type.map(i => i.count), 1);

  const top3Severity = [...(analytics?.incidentsBySeverity || [])].sort((a, b) => b.count - a.count).slice(0, 3);
  const maxSevCount  = Math.max(...top3Severity.map(i => i.count), 1);

  const top3Students = [...(analytics?.topStudents || [])].sort((a, b) => b.incidentCount - a.incidentCount).slice(0, 3);

  // ── Styles ───────────────────────────────────────────────────────────────────

  const cardCls  = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls  = dm ? 'text-white' : 'text-slate-500';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';

  const pillCls = (active: boolean) =>
    `btn-glass btn-glass-pill${active ? ' btn-glass-blue' : ''}`;

  return (
    <div className="space-y-6" data-dm={dm}>

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className={`text-xl font-bold ${titleCls}`}>Analytics & Reports</h2>
          <p className={`text-sm ${descCls}`}>{inputToDisplay(fromDate)} – {inputToDisplay(toDate)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => doFetch(preset === 'custom' ? diffDays(fromDate, toDate) : (PRESETS.find(p => p.key === preset)?.days ?? 90))}
            disabled={loading} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button onClick={handleExportCSV} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Date range controls ── */}
      <div className={`border rounded-xl px-4 py-3 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-center gap-2">

          {/* Preset pills */}
          {PRESETS.map(({ key, label }) => (
            <button key={key} onClick={() => handlePresetChange(key)} className={pillCls(preset === key)}>
              {label}
            </button>
          ))}

          {/* Divider */}
          <div className={`w-px h-5 self-center mx-1 ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />

          {/* From / To + Apply — dimmed unless Custom is active */}
          <div className={`flex items-center gap-2 transition-opacity ${preset !== 'custom' ? 'opacity-40 pointer-events-none' : ''}`}>
            <input
              type="date"
              value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPreset('custom'); }}
              className={`text-xs px-3 rounded-full outline-none border ${dm ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-700'}`}
              style={{ padding: '5px 12px' }}
            />
            <span className={`text-xs ${mutedCls}`}>–</span>
            <input
              type="date"
              value={toDate}
              onChange={e => { setToDate(e.target.value); setPreset('custom'); }}
              className={`text-xs px-3 rounded-full outline-none border ${dm ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-700'}`}
              style={{ padding: '5px 12px' }}
            />
            <button
              onClick={handleApply}
              disabled={!fromDate || !toDate}
              className="btn-glass btn-glass-pill btn-glass-blue disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dm ? 'border-slate-400' : 'border-slate-800'}`} />
        </div>
      ) : !analytics ? (
        <Card className={cardCls}>
          <CardContent className="py-12">
            <p className={`text-center text-sm ${mutedCls}`}>No analytics data available</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Overview stats ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Total Incidents</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{analytics.overview.totalIncidents}</p>
                    <p className={`text-xs flex items-center gap-1 ${analytics.overview.incidentTrend > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {analytics.overview.incidentTrend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {Math.abs(analytics.overview.incidentTrend)}% vs prior period
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-red-900/50' : 'bg-red-100'}`}>
                    <Activity className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Active Alerts</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{analytics.overview.activeAlerts}</p>
                    <p className={`text-xs flex items-center gap-1 ${analytics.overview.alertTrend > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {analytics.overview.alertTrend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {Math.abs(analytics.overview.alertTrend)}% vs prior period
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Shield className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Resolution Rate</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>
                      {analytics.overview.totalIncidents > 0
                        ? `${Math.round(((analytics.overview.totalIncidents - analytics.overview.activeAlerts) / analytics.overview.totalIncidents) * 100)}%`
                        : '—'}
                    </p>
                    <p className={`text-xs ${mutedCls}`}>Incidents resolved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Top 3 Type + Top 3 Severity ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <BarChart3 className="h-4 w-4" /> Top 3 Incident Types
                </CardTitle>
                <CardDescription className={descCls}>Highest volume categories this period</CardDescription>
              </CardHeader>
              <CardContent>
                {top3Type.length > 0 ? (
                  <div className="space-y-4">
                    {top3Type.map((item, i) => (
                      <div key={item.type} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${
                              i === 0 ? 'bg-red-500' : i === 1 ? 'bg-orange-400' : 'bg-amber-400'
                            }`}>
                              {i + 1}
                            </span>
                            <span className={`font-medium capitalize ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                              {item.type.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className={`font-semibold tabular-nums ${dm ? 'text-white' : 'text-slate-700'}`}>{item.count}</span>
                        </div>
                        <div className={`h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${TYPE_COLORS[item.type] || 'bg-blue-500'}`}
                            style={{ width: `${Math.round((item.count / maxTypeCount) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${mutedCls}`}>No incident data for this period</p>
                )}
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <Target className="h-4 w-4" /> Top 3 Severity Levels
                </CardTitle>
                <CardDescription className={descCls}>Most common severity distributions</CardDescription>
              </CardHeader>
              <CardContent>
                {top3Severity.length > 0 ? (
                  <div className="space-y-4">
                    {top3Severity.map((item, i) => {
                      const sev = item.severity.toLowerCase();
                      const pct = Math.round((item.count / (analytics.overview.totalIncidents || 1)) * 100);
                      return (
                        <div key={item.severity} className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${SEVERITY_BAR[sev] || 'bg-slate-400'}`}>
                                {i + 1}
                              </span>
                              <span className={`font-medium capitalize ${SEVERITY_TEXT[sev] || (dm ? 'text-white' : 'text-slate-700')}`}>
                                {item.severity}
                              </span>
                            </div>
                            <span className={`font-semibold tabular-nums ${dm ? 'text-white' : 'text-slate-700'}`}>
                              {item.count}{' '}
                              <span className={`font-normal text-xs ${mutedCls}`}>({pct}%)</span>
                            </span>
                          </div>
                          <div className={`h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${SEVERITY_BAR[sev] || 'bg-slate-400'}`}
                              style={{ width: `${Math.round((item.count / maxSevCount) * 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${mutedCls}`}>No severity data for this period</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Incident Trend Line Chart ── */}
          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                <Calendar className="h-4 w-4" /> Incident Trend
              </CardTitle>
              <CardDescription className={descCls}>
                {inputToDisplay(fromDate)} – {inputToDisplay(toDate)} · {analytics.incidentsByMonth.length} data points
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.incidentsByMonth.length > 0 ? (
                <LineTrendChart data={analytics.incidentsByMonth} dm={dm} />
              ) : (
                <p className={`text-sm text-center py-10 ${mutedCls}`}>No trend data for this period</p>
              )}
            </CardContent>
          </Card>

          {/* ── Top 3 At-Risk Students + Assigned Teachers ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <GraduationCap className="h-4 w-4" /> Top 3 At-Risk Students
                </CardTitle>
                <CardDescription className={descCls}>Highest incident count this period</CardDescription>
              </CardHeader>
              <CardContent>
                {top3Students.length > 0 ? (
                  <div className="space-y-3">
                    {top3Students.map((student, i) => {
                      const teacher  = studentTeachers.get(student.name);
                      const rankBg   = [dm ? '#7f1d1d' : '#fee2e2', dm ? '#7c2d12' : '#ffedd5', dm ? '#1c3461' : '#dbeafe'][i];
                      const rankText = [dm ? '#f87171' : '#dc2626', dm ? '#fb923c' : '#ea580c', dm ? '#60a5fa' : '#2563eb'][i];
                      return (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${dm ? 'border-slate-700 bg-slate-700/30' : 'border-slate-100 bg-slate-50'}`}>
                          <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{ backgroundColor: rankBg, color: rankText }}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{student.name}</p>
                            {teacher ? (
                              <p className={`text-xs flex items-center gap-1 mt-0.5 ${mutedCls}`}>
                                <BookOpen className="h-3 w-3 shrink-0" /> {teacher}
                              </p>
                            ) : (
                              <p className={`text-xs mt-0.5 ${mutedCls}`}>Classroom data unavailable</p>
                            )}
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                            student.incidentCount >= 5
                              ? (dm ? 'bg-red-900/50 text-red-400'    : 'bg-red-100 text-red-700')
                              : student.incidentCount >= 3
                              ? (dm ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-700')
                              : (dm ? 'bg-blue-900/50 text-blue-400'   : 'bg-blue-100 text-blue-700')
                          }`}>
                            {student.incidentCount} incidents
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${mutedCls}`}>No at-risk students this period</p>
                )}
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <Users className="h-4 w-4" /> Assigned Teachers
                </CardTitle>
                <CardDescription className={descCls}>Teachers responsible for at-risk student classrooms</CardDescription>
              </CardHeader>
              <CardContent>
                {top3Students.length > 0 ? (
                  <div className="space-y-3">
                    {top3Students.map((student, i) => {
                      const teacher = studentTeachers.get(student.name);
                      return (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${dm ? 'border-slate-700 bg-slate-700/30' : 'border-slate-100 bg-slate-50'}`}>
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                            <Users className="h-4 w-4 text-emerald-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${dm ? 'text-slate-200' : 'text-slate-800'}`}>
                              {teacher || 'Unassigned'}
                            </p>
                            <p className={`text-xs truncate mt-0.5 ${mutedCls}`}>Student: {student.name}</p>
                          </div>
                          {!teacher && (
                            <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${dm ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500'}`}>N/A</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${mutedCls}`}>No data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
